import { useTranslation } from 'react-i18next';

import InfoIcon from '@mui/icons-material/Info';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { OverridableStringUnion } from '@mui/types';

import {
  useHostingAdvancedServer,
  useHostingInfoDiskUsage,
} from '@newfold/huapi-js';

import useAccount from '~/hooks/useAccount';
import {
  productTypes,
  useCtbProductInfo,
  useCtbPurchaseStatusQuery,
} from '~/hooks/useCtbProductInfo';
import useFeature from '~/hooks/useFeature';
import useHostingInfo from '~/hooks/useHostingInfo';
import useQueryFeature from '~/hooks/useQueryFeature';

import AdvancedHostingQuickLinks from '../QuickLinks/QuickLinks';
import AdvancedHostingUsageOutOfSpace from './UsageOutOfSpace';
import AdvancedHostingUsageView from './UsageView';

interface HostingData {
  disklimit: string;
  diskused: string;
}

type DiskValue = {
  percentUsed: number;
  variant: OverridableStringUnion<'success' | 'error' | 'warning' | 'primary'>;
};

export type AdvancedHostingDiskUsageStatsPropOptions = {
  canAccessEmail?: boolean;
};

const AdvancedHostingDiskUsageStats = ({
  canAccessEmail = false,
}: AdvancedHostingDiskUsageStatsPropOptions) => {
  const { id: accountId } = useAccount();
  const { t } = useTranslation('settings', { keyPrefix: 'usageStatsCard' });

  const { isDisabled } = useHostingInfo();

  const [isPlesk, , clientReady] = useFeature('plesk');
  const [isWindows] = useFeature('windows');
  const [showServerControls, , isClientReady] = useFeature('server_controls');
  const theme = useTheme();
  const { data: serverData } = useHostingAdvancedServer(accountId, {
    query: {
      refetchOnWindowFocus: false,
      enabled: !!accountId && isClientReady && showServerControls,
    },
  });
  const isDiskUsageWarningEnabled = useQueryFeature('diskusagewarning');
  // CTB upgrade status
  const productInfo = useCtbProductInfo(productTypes.HOSTING_UPGRADE);
  const { hasPurchasedRecently } = useCtbPurchaseStatusQuery(
    productInfo.purchaseQueryKey,
  );
  const doNotShowDiskUsage = isPlesk || isWindows || !clientReady;
  const isRestrictDiskUsageCall = isDisabled ? false : !doNotShowDiskUsage;

  const { data, isFetching, isError } = useHostingInfoDiskUsage(accountId, {
    query: {
      enabled: !!accountId && isRestrictDiskUsageCall,
    },
  });
  const diskUsageData = data?.data as HostingData;

  let diskValues = {
    percentUsed: 0,
    variant: 'primary',
  } as DiskValue;

  if (diskUsageData && diskUsageData?.disklimit !== 'Unlimited') {
    const percentUsed =
      (Number(diskUsageData?.diskused.slice(0, -2)) /
        Number(diskUsageData?.disklimit.slice(0, -2))) *
      100;
    let variant = 'success';
    if (percentUsed >= 85) variant = 'error';
    if (percentUsed < 85 && percentUsed >= 70) variant = 'warning';

    diskValues = { percentUsed, variant } as DiskValue;
  }

  if (
    hasPurchasedRecently ||
    isError ||
    serverData?.data?.status === 'stopped'
  ) {
    return null;
  }

  if (diskValues.percentUsed >= 85) {
    return (
      <Grid container spacing={3}>
        {!doNotShowDiskUsage && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Grid container spacing={2} columns={2}>
                  <Grid item md={2} lg={1}>
                    <Stack spacing={3}>
                      <Stack spacing={1}>
                        <Typography variant="h3">{t('subTitle')}</Typography>
                        <Typography variant="body2">
                          {isFetching ? (
                            <Skeleton width="80%" />
                          ) : (
                            t('advHostingDesc', {
                              percentUsed: (diskValues?.percentUsed).toFixed(2),
                            })
                          )}
                        </Typography>
                      </Stack>
                      <Stack>
                        <AdvancedHostingUsageView
                          isFetching={isFetching}
                          value={diskValues}
                          disklimit={diskUsageData?.disklimit}
                          diskused={diskUsageData?.diskused}
                        />
                      </Stack>
                    </Stack>
                  </Grid>
                  <Grid item md={2} lg={1}>
                    <AdvancedHostingUsageOutOfSpace />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
        <Grid item xs={12}>
          <AdvancedHostingQuickLinks canAccessEmail={canAccessEmail} />
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3}>
      {!doNotShowDiskUsage && (
        <Grid item xs={12} sm={6}>
          <Card variant="outlined">
            <CardHeader
              titleTypographyProps={{ variant: 'h3' }}
              title={t('subTitle')}
              subheader={
                isFetching ? (
                  <Skeleton width="80%" />
                ) : (
                  t('advHostingDesc', {
                    percentUsed: (diskValues?.percentUsed).toFixed(2),
                  })
                )
              }
              subheaderTypographyProps={{ variant: 'body2' }}
            />
            <CardContent>
              {diskValues?.percentUsed === 0 && isDiskUsageWarningEnabled ? (
                <Stack spacing={2}>
                  <Stack spacing={1} direction="row" alignItems="center">
                    <Typography variant="h3">{t('notAvailable')}</Typography>
                    <Typography
                      variant="h4"
                      sx={{ color: theme.palette.error.main }}
                    >
                      {t('failedConnection')}
                    </Typography>
                    <Tooltip
                      arrow
                      placement="top-start"
                      title={
                        <>
                          <Typography variant="body2">
                            {t('failedConnectionTooltip')}
                          </Typography>
                          <Typography variant="body2">
                            {t('noteFailedConnectionTooltip')}
                          </Typography>
                        </>
                      }
                      sx={{
                        color: theme.palette.error.main,
                        whiteSpace: 'pre-wrap',
                      }}
                      PopperProps={{
                        disablePortal: true,
                      }}
                    >
                      <InfoIcon color="action" />
                    </Tooltip>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={0}
                    color={'info'}
                  />
                </Stack>
              ) : (
                <AdvancedHostingUsageView
                  isFetching={isFetching}
                  value={diskValues}
                  disklimit={diskUsageData?.disklimit}
                  diskused={diskUsageData?.diskused}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      )}
      <Grid item xs={12} sm={doNotShowDiskUsage ? 0 : 6}>
        <AdvancedHostingQuickLinks canAccessEmail={canAccessEmail} />
      </Grid>
    </Grid>
  );
};
export default AdvancedHostingDiskUsageStats;
